// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace chrome {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  export namespace runtime {
    export function sendMessage(
      extensionId: string,
      message: any,
      callback?: (response: any) => void
    ): void
  }
}

interface ExtensionData {
  type: 'AUTH_TOKEN' | 'CHECK_EXTENSION' | 'LOGOUT'
  token?: string
  refreshToken?: string
}

interface ExtensionResponse {
  installed?: boolean
}

/**
 * Communicates with a Chrome extension
 * @param data Data to send to the extension
 * @returns Promise that resolves with the extension's response
 */
export const extService = (data: ExtensionData): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    getExtensionId()
      .then((extensionId) => {
        if (!extensionId) {
          resolve(false)
          return
        }

        if (typeof chrome !== 'undefined' && chrome && chrome.runtime && chrome.runtime.sendMessage) {
          switch (data.type) {
            case 'AUTH_TOKEN':
              chrome.runtime.sendMessage(extensionId, {
                token: data.token,
                refreshToken: data.refreshToken,
                type: 'AUTH_TOKEN',
              })
              resolve(true) // Assuming successful execution
              break
            case 'CHECK_EXTENSION':
              chrome.runtime.sendMessage(
                extensionId,
                'CHECK_EXTENSION',
                (response: ExtensionResponse) => {
                  if (response && response.installed) {
                    resolve(true)
                  } else {
                    resolve(false)
                  }
                }
              )
              break
            case 'LOGOUT':
              chrome.runtime.sendMessage(extensionId, { type: 'LOGOUT' })
              resolve(true) // Assuming successful execution
              break
            default:
              resolve(false)
          }
        } else {
          resolve(false)
        }
      })
      .catch((error: Error) => {
        console.warn('Extension connection failed:', error.message)
        resolve(false)
      })
  })
}

/**
 * Gets the Chrome Extension ID.
 *
 * Source priority (industry pattern — Doppler drives everything):
 *   1. Backend public-config API  ← primary source (Doppler → backend → frontend)
 *   2. VITE_CHROME_EXTENSION_ID   ← local dev override only
 *
 * The backend serves the ID from process.env.CHROME_EXTENSION_ID (Doppler secret).
 * This function calls fetchPublicConfig which caches the result in memory,
 * so the network call only happens once per page load.
 */
const getExtensionId = (): Promise<string | null> => {
  const localOverride = import.meta.env.VITE_CHROME_EXTENSION_ID as string
  if (localOverride) return Promise.resolve(localOverride)

  return import('@/api/apis/public-config')
    .then(({ fetchPublicConfig }) => fetchPublicConfig())
    .then((config) => config.chromeExtension.id || null)
    .catch((error: Error) => {
      console.warn('[ExtService] Failed to fetch extension ID from backend:', error.message)
      return null
    })
}
